// src/stores/useAuthStore.ts
// Central auth state — single source of truth for user, profile, sync, and
// modal visibility. No Firebase calls here — all side effects live in
// authService.ts and useFirebaseAuth.ts.

import { create } from 'zustand';
import type { User } from 'firebase/auth';

// ── Public types ─────────────────────────────────────────────────────────────

export type AuthTab = 'login' | 'signup' | 'forgot';

export interface SubscriptionInfo {
  status: 'active' | 'inactive' | 'expired';
  plan: 'monthly' | 'three_months' | 'yearly' | null;
  startDate: Date | null;
  endDate: Date | null;
  isLifetime?: boolean;
  promoCode?: string;
}

export interface UserProfile {
  displayName: string;
  email: string;
  phone: string;
  occupation: string;
  institution: string;
  avatarUrl: string | null;
  avatarStoragePath: string | null;
  authProvider: 'google' | 'email' | null;
  hasFullAccess: boolean;
  subscription: SubscriptionInfo;
  createdAt: string;
}

export interface SyncStatus {
  isSyncing: boolean;
  lastSync: number | null;
  /** Renamed from pendingCount — matches handoff spec. */
  pendingItems: number;
}

// ── Store interface ──────────────────────────────────────────────────────────

interface AuthState {
  /** Authenticated Firebase user. null when signed-out or anonymous. */
  user: User | null;
  /** True once onAuthStateChanged has fired at least once. */
  authLoaded: boolean;
  /** Firestore profile document. Loaded after sign-in, cleared on sign-out. */
  profile: UserProfile | null;
  /** Sync state populated by useFirebaseAuth from useProgressSync hook. */
  syncStatus: SyncStatus;
  /** Prevents concurrent logout attempts. */
  isLoggingOut: boolean;
  /**
   * When true, the logout button is blocked until the exercise is finished.
   * Set by Session 5's exercise store.
   */
  isExerciseActive: boolean;

  // ── Modal visibility ────────────────────────────────────────────────────
  authModalOpen: boolean;
  authModalTab: AuthTab;
  profileModalOpen: boolean;
  deleteModalOpen: boolean;

  // ── Actions ─────────────────────────────────────────────────────────────
  setUser: (user: User | null) => void;
  setAuthLoaded: (v: boolean) => void;
  setProfile: (profile: UserProfile | null) => void;
  setSyncStatus: (patch: Partial<SyncStatus>) => void;
  setIsLoggingOut: (v: boolean) => void;
  setIsExerciseActive: (v: boolean) => void;

  openAuthModal: (tab?: AuthTab) => void;
  closeAuthModal: () => void;
  setAuthTab: (tab: AuthTab) => void;

  openProfileModal: () => void;
  closeProfileModal: () => void;

  openDeleteModal: () => void;
  closeDeleteModal: () => void;
}

// ── Store ────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  authLoaded: false,
  profile: null,
  syncStatus: { isSyncing: false, lastSync: null, pendingItems: 0 },
  isLoggingOut: false,
  isExerciseActive: false,
  authModalOpen: false,
  authModalTab: 'login',
  profileModalOpen: false,
  deleteModalOpen: false,

  setUser: (user) => set({ user }),
  setAuthLoaded: (v) => set({ authLoaded: v }),
  setProfile: (profile) => set({ profile }),
  setSyncStatus: (patch) =>
    set((s) => ({ syncStatus: { ...s.syncStatus, ...patch } })),
  setIsLoggingOut: (v) => set({ isLoggingOut: v }),
  setIsExerciseActive: (v) => set({ isExerciseActive: v }),

  openAuthModal: (tab = 'login') => set({ authModalOpen: true, authModalTab: tab }),
  closeAuthModal: () => set({ authModalOpen: false }),
  setAuthTab: (tab) => set({ authModalTab: tab }),

  openProfileModal: () => set({ profileModalOpen: true }),
  closeProfileModal: () => set({ profileModalOpen: false }),

  openDeleteModal: () => set({ deleteModalOpen: true }),
  closeDeleteModal: () => set({ deleteModalOpen: false }),
}));